import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MousePointerClick } from "lucide-react";
import { STOPS } from "@/components/Roadmap";
import { playCoin, playJackpot } from "@/sound";

const EASE = [0.22, 1, 0.36, 1];
const MAX = 1_000_000;
const barHeight = (v) => Math.round(64 + 210 * Math.sqrt(v / MAX));

export default function MilestoneChart() {
  const [selected, setSelected] = useState(STOPS.length - 1);
  const active = STOPS[selected];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.85, ease: EASE }}
      className="relative mt-16 overflow-hidden rounded-3xl border border-[rgba(229,169,60,0.45)] bg-gradient-to-b from-[#10121A] to-[#0A0C10] p-6 shadow-[0_0_70px_rgba(229,169,60,0.10)] sm:p-10"
      data-testid="milestone-chart"
      id="milestone-chart"
    >
      <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-[560px] -translate-x-1/2 rounded-full bg-[#E5A93C] opacity-[0.08] blur-[90px]" />

      <div className="relative flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-[#F8FAFC] sm:text-2xl">
          The Milestone <span className="text-[#F6C85F]">Chart</span>
        </h3>
        <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
          <MousePointerClick className="h-3.5 w-3.5 text-[#E5A93C]" />
          Tap a bar to inspect the rewards
        </span>
      </div>

      {/* Bars */}
      <div className="relative mt-10 flex items-end gap-2 sm:gap-4" data-testid="milestone-chart-bars">
        {STOPS.map((s, i) => {
          const isActive = i === selected;
          return (
            <button
              key={s.mc}
              onClick={() => {
                setSelected(i);
                if (s.finale) playJackpot();
                else playCoin();
              }}
              data-testid={`milestone-bar-${i + 1}`}
              className="group flex min-w-0 flex-1 flex-col items-center gap-3 outline-none"
              aria-pressed={isActive}
            >
              <div className="relative flex w-full items-end justify-center" style={{ height: 280 }}>
                <motion.div
                  initial={{ height: 0 }}
                  whileInView={{ height: barHeight(s.value) }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.15 + i * 0.1 }}
                  className={`relative w-full max-w-[88px] rounded-t-xl border transition-colors duration-300 ${
                    isActive
                      ? "border-[#F6C85F] bg-gradient-to-t from-[#B8860B] via-[#E5A93C] to-[#F6C85F] shadow-[0_0_34px_rgba(246,200,95,0.45)]"
                      : "border-[rgba(229,169,60,0.25)] bg-[#0F1E36] group-hover:border-[rgba(246,200,95,0.6)] group-hover:bg-[#16263F]"
                  }`}
                >
                  {isActive && (
                    <span className="absolute -top-2 left-1/2 flex h-4 w-4 -translate-x-1/2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F6C85F] opacity-70" />
                      <span className="relative inline-flex h-4 w-4 rounded-full border-2 border-[#07080A] bg-[#F6C85F]" />
                    </span>
                  )}
                </motion.div>
              </div>
              <span
                className={`truncate font-mono2 text-[9px] font-bold tracking-wider transition-colors duration-300 sm:text-[11px] ${
                  isActive ? "text-[#F6C85F]" : "text-[#64748B] group-hover:text-[#F6C85F]"
                }`}
              >
                {s.mc}
              </span>
            </button>
          );
        })}
      </div>

      {/* Detail panel */}
      <AnimatePresence mode="wait">
        <motion.div
          key={selected}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.35, ease: EASE }}
          className={`relative mt-8 rounded-2xl border p-6 sm:p-7 ${
            active.finale
              ? "border-[rgba(246,200,95,0.6)] bg-gradient-to-br from-[#141720] to-[#0F1E36]"
              : "border-[rgba(229,169,60,0.3)] bg-[#0E1015]"
          }`}
          data-testid="milestone-detail"
        >
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`rounded-full px-4 py-1.5 font-mono2 text-xs font-bold tracking-wider ${
                active.finale
                  ? "bg-[#E5A93C] text-[#07080A]"
                  : "border border-[rgba(229,169,60,0.4)] text-[#F6C85F]"
              }`}
              data-testid="milestone-detail-mc"
            >
              {active.mc}
            </span>
            <h4 className="font-display text-lg font-extrabold uppercase tracking-tight text-[#F8FAFC] sm:text-xl">
              {active.title}
            </h4>
          </div>
          <ul className="mt-4 flex flex-wrap gap-x-8 gap-y-2.5">
            {active.rewards.map((r) => (
              <li key={r.text} className="flex items-center gap-2.5 text-sm text-[#94A3B8] sm:text-base">
                <r.icon className="h-4 w-4 shrink-0 text-[#E5A93C]" />
                {r.text}
              </li>
            ))}
          </ul>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
